import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { communityService } from '../../services/api';
import ScheduleHeatmap from './ScheduleHeatmap';

/**
 * Sección "Mis horarios" / "Dónde encontrar a X" para los profiles
 * (propio y ajeno). Muestra el calendario semanal directo (no un chip
 * colapsado) — si la persona tiene horarios en varias comunidades
 * compartidas, hay tabs arriba para cambiar.
 *
 * Si no hay schedules visibles para el viewer, devuelve null y la
 * sección no se renderea.
 *
 * Props:
 *   userId
 *   userName: opcional para el copy del header
 *   isOwn: bool — copy "Mis horarios" vs "Dónde encontrar a {name}"
 */
function UserSchedulesSection({ userId, userName, isOwn = false }) {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(0);

  const { data: entries = [], isLoading: entriesLoading } = useQuery({
    queryKey: ['user-schedules', String(userId)],
    queryFn: async () => (await communityService.listSchedulesByUser(userId)).data || [],
    enabled: !!userId,
    staleTime: 60_000,
  });

  // Resetear active al cambiar de target.
  useEffect(() => { setActiveIdx(0); }, [userId]);

  const safe = entries.filter((e) => e && e.communityId);
  const active = safe[activeIdx] || safe[0];

  const { data: schedule, isLoading: scheduleLoading } = useQuery({
    queryKey: ['user-schedule', String(active?.communityId), String(userId)],
    queryFn: async () => (await communityService.getUserScheduleInCommunity(active.communityId, userId)).data,
    enabled: !!active && !!userId,
    staleTime: 60_000,
  });

  if (entriesLoading) return null;

  // Empty state — propio: card invitando a configurar. Ajeno: ocultar.
  if (safe.length === 0) {
    if (!isOwn) return null;
    return (
      <section className="bg-surface-container rounded-2xl px-6 py-8">
        <div className="flex flex-col items-center text-center gap-3">
          <span className="w-14 h-14 rounded-2xl bg-primary-container flex items-center justify-center shrink-0 shadow-sm">
            <span className="material-symbols-outlined text-[28px] text-primary">schedule</span>
          </span>
          <h2 className="text-lg font-bold text-on-surface">{t('Mis horarios')}</h2>
          <p className="text-sm text-on-surface-variant leading-snug max-w-[42ch]">
            {t('Declará en qué días y horarios estás disponible en las comunidades que lo permitan.')}
          </p>
          <p className="text-[12px] text-on-surface-variant/80">
            {t('Configurálos desde la página de cada comunidad.')}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-surface-container rounded-2xl p-5">
      <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2 mb-3">
        <span className="material-symbols-outlined text-[20px] text-primary">schedule</span>
        {isOwn ? t('Mis horarios') : t('Dónde encontrar a {{name}}', { name: userName || '' })}
      </h2>

      {/* Tabs por comunidad si hay > 1 — sino, mostrar el nombre como header chico. */}
      {safe.length > 1 ? (
        <div className="flex gap-1 mb-3 overflow-x-auto hide-scrollbar -mx-1 px-1">
          {safe.map((e, i) => (
            <button
              key={e.communityId}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                i === activeIdx
                  ? 'bg-primary text-on-primary'
                  : 'text-on-surface-variant hover:bg-surface-container-high/50'
              }`}
            >
              {e.communityName}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-on-surface-variant mb-3 truncate">
          <span className="material-symbols-outlined text-[12px] align-text-bottom mr-1">groups</span>
          {safe[0].communityName}
          {safe[0].sedes && safe[0].sedes.length > 0 && (
            <span className="text-on-surface-variant/70"> · {safe[0].sedes.join(' · ')}</span>
          )}
        </p>
      )}

      {scheduleLoading ? (
        <div className="h-32 rounded-lg bg-surface-container-high/30 animate-pulse" />
      ) : schedule && Array.isArray(schedule.days) ? (
        <ScheduleHeatmap days={schedule.days} sedes={active?.sedes || []} />
      ) : (
        <p className="text-xs text-on-surface-variant/60 italic">{t('Sin horarios.')}</p>
      )}
    </section>
  );
}

export default UserSchedulesSection;
